// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import * as StringUtil from 'app/common/lib/StringUtil';
import getUserAuthBehaviorDataMap from 'app/common/lib/util/userBehaviorDataUtils';
import i18n from 'app/common/lib/i18n';
import NuxPickerInterestsGridContainer from 'app/common/react/components/growth/activation/NuxPicker/NuxPickerInterestsGridContainer';
import throttle from 'lodash/throttle';
import type { Dispatch } from 'redux';
import type { Experiments } from 'app/common/lib/react/deprecated/legacyWithExperiments'; // eslint-disable-line pinterest/no-deprecated-imports
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { Experiment } from 'app/common/react/components/experiments';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { track } from 'app/common/lib/react/analytics';
import setNuxProgress, { type NuxStep } from 'app/common/redux/actions/nuxProgress';
import {
  type InterestObj,
  DEFAULT_NUM_TO_FOLLOW,
  MIN_HF_STEP_WIDTH,
  NUMBER_SELECTED_AT_BOTTOM_THRESHOLD,
  NUX_TOPIC_PICKER_STEP_STATUS,
  SHOWED,
  COMPLETED,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import {
  updateFollowedInterests,
  setUserState,
  submitOrientationContextData,
} from 'app/common/redux/actions/nux/updateUser';
import { Box, Button, Heading, Text } from 'gestalt';

const defaultPickButtonTextGenerator = (numberLeftToFollow: number) =>
  i18n.ngettext(
    'Pick {{ numberLeftToFollow }} more',
    'Pick {{ numberLeftToFollow }} more',
    numberLeftToFollow,
    'number of interests left to pick',
  );

type AltText = {|
  nextButtonText?: string,
  nextButtonTextSingleTopic?: string,
  completionNextButtonText?: string,
|};

type OwnProps = {|
  categorized?: boolean,
  completionNextButtonText?: string,
  customProcessFollowedInterests?: () => void,
  experiments: Experiments,
  followedInterests: Array<InterestObj>,
  incrementNuxStep: () => void,
  isRenux?: boolean,
  isSimpleRenux?: boolean, // only has [Picker, Loader], used by topic education
  limited?: boolean,
  nextButtonText?: string,
  nextButtonTextSingleTopic?: string,
  numberToFollow?: number,
  onSelectedInterestsChange: () => void,
  personalizationCopy: {
    headerText: string,
    subHeaderText: string,
  },
  pickButtonTextGenerator: (numberLeftToFollow: number) => string,
  preselect: boolean,
  selectedInterests: Array<InterestObj>,
  showTableOfContents?: boolean,
  skipButton: boolean,
|};

type ReduxStateProps = {|
  gender: string,
|};

type DispatchProps = {|
  dispatchSetNuxStepProgress: (NuxStep) => void,
  setNuxPickerProgress: (number) => void,
  dispatchUpdateFollows: ({
    [interestId: number]: boolean,
  }) => Promise<any>,
  dispatchSubmitOrientationContext: (Object) => Promise<any>,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...DispatchProps,
|};

type NuxPickerState = {|
  buttonText: string,
  isDisabled: boolean,
  allowSkip: boolean,
|};

const FOOTER_FADE = (
  <Box
    dangerouslySetInlineStyle={{
      __style: {
        background: 'linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
      },
    }}
    height={40}
    left
    marginTop={-10}
    position="absolute"
    right
    top
  />
);

export class NuxPicker extends Component<Props, NuxPickerState> {
  static defaultProps = {
    skipButton: false,
    pickButtonTextGenerator: defaultPickButtonTextGenerator,
  };

  constructor(props: Props) {
    super(props);
    const {
      nextButtonText,
      nextButtonTextSingleTopic,
      completionNextButtonText,
      isRenux,
      selectedInterests,
    } = props;
    const altText = { nextButtonText, nextButtonTextSingleTopic, completionNextButtonText };
    const buttonState = this.getButtonState(
      selectedInterests,
      props.numberToFollow || DEFAULT_NUM_TO_FOLLOW,
      props.skipButton,
      altText,
    );

    this.state = { ...buttonState };
    this.viewType = isRenux
      ? PCONST.thrift.ViewType.REDO_ORIENTATION
      : PCONST.thrift.ViewType.ORIENTATION;
  }

  componentDidMount() {
    const { dispatchSetNuxStepProgress, isRenux, setNuxPickerProgress } = this.props;
    if (!isRenux) {
      setNuxPickerProgress(SHOWED);
    }
    dispatchSetNuxStepProgress('topic');
    track(PCONST.thrift.EventType.VIEW, {
      view: this.viewType,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
    });
  }

  componentDidUpdate(prevProps: Props) {
    const {
      numberToFollow,
      selectedInterests,
      skipButton,
      nextButtonText,
      nextButtonTextSingleTopic,
      completionNextButtonText,
    } = this.props;

    if (selectedInterests.length !== prevProps.selectedInterests.length) {
      const altText = { nextButtonText, nextButtonTextSingleTopic, completionNextButtonText };
      const buttonState = this.getButtonState(
        selectedInterests,
        numberToFollow || DEFAULT_NUM_TO_FOLLOW,
        skipButton,
        altText,
      );

      this.setButtonState(buttonState);
    }
  }

  setButtonState(buttonState: { buttonText: string, isDisabled: boolean, allowSkip: boolean }) {
    this.setState({ ...buttonState });
  }

  getButtonState = (
    selectedInterests: Array<InterestObj>,
    numberToFollow: number,
    canSkip: boolean,
    altText: AltText,
    isBottom: boolean = false,
  ) => {
    const { isRenux, isSimpleRenux, pickButtonTextGenerator } = this.props;
    const numberOfSelectedInterests = selectedInterests.length;

    const enableDone = isBottom && numberOfSelectedInterests >= NUMBER_SELECTED_AT_BOTTOM_THRESHOLD;

    const numberLeftToFollow = enableDone
      ? 0
      : Math.max(0, numberToFollow - numberOfSelectedInterests);

    const { nextButtonText, completionNextButtonText } = altText;

    const pickButtonText = pickButtonTextGenerator(numberLeftToFollow);
    const buttonText = {
      done: {
        buttonText: completionNextButtonText || i18n._('Done'),
        isDisabled: false,
        allowSkip: false,
      },
      more: {
        buttonText:
          isRenux && !isSimpleRenux
            ? i18n._('Next', 'go to next step')
            : StringUtil.safeFormat(nextButtonText || pickButtonText, {
                numberLeftToFollow: `${numberLeftToFollow}`,
              }),
        isDisabled: true,
        allowSkip: false,
      },
      skip: {
        buttonText: i18n._('Skip (pick topics for me)'),
        isDisabled: false,
        allowSkip: true,
      },
    };

    if (numberLeftToFollow === 0) {
      return buttonText.done;
    }

    if (canSkip) {
      return buttonText.skip;
    }

    return buttonText.more;
  };

  viewType: $Values<PCONST.thrift.ViewType>;

  handleBottomScroll = (isBottom: boolean) => {
    const {
      numberToFollow,
      selectedInterests,
      skipButton,
      nextButtonText,
      nextButtonTextSingleTopic,
      completionNextButtonText,
    } = this.props;
    const altText = { nextButtonText, nextButtonTextSingleTopic, completionNextButtonText };
    const buttonState = this.getButtonState(
      selectedInterests,
      numberToFollow || DEFAULT_NUM_TO_FOLLOW,
      skipButton,
      altText,
      isBottom,
    );
    this.setState({ ...this.state, ...buttonState });
  };

  handleBottomScrollThrottled = throttle(this.handleBottomScroll, 50);

  handleNextClick = () => {
    const {
      customProcessFollowedInterests,
      isRenux,
      selectedInterests,
      followedInterests,
      incrementNuxStep,
      setNuxPickerProgress,
      dispatchUpdateFollows,
      dispatchSubmitOrientationContext,
    } = this.props;
    // TODO (briantran): Refactor handleNextClick into smaller functions
    // organized by function
    if (customProcessFollowedInterests) {
      if (!isRenux) {
        setNuxPickerProgress(COMPLETED);
      }
      customProcessFollowedInterests();
      incrementNuxStep();
      return;
    }
    let interestFollowPromise;
    if (isRenux) {
      const interestFollowingMapping = followedInterests.reduce((memo, interest) => {
        memo[interest.id] = interest.is_followed;
        return memo;
      }, {});
      interestFollowPromise = Promise.resolve(dispatchUpdateFollows(interestFollowingMapping));
    } else {
      const interestIds = [];
      const logData = [];

      selectedInterests.forEach((interest) => {
        interestIds.push(interest.id);
        logData.push(interest.log_data ? interest.log_data : null);
      });
      const orientationData = {
        interests: interestIds.join(),
        log_data: JSON.stringify(logData),
        referrer: 'nux',
        user_behavior_data: getUserAuthBehaviorDataMap(),
      };
      interestFollowPromise = Promise.resolve(dispatchSubmitOrientationContext(orientationData));
    }

    interestFollowPromise.then(() => {
      if (!isRenux) {
        setNuxPickerProgress(COMPLETED);
      }
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.BROWSER,
        element: PCONST.thrift.ElementType.NEXT_BUTTON,
        view: this.viewType,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
      });
      incrementNuxStep();
    });
  };

  renderOnHomefeed = () => {
    const { headerText } = this.props.personalizationCopy;
    const { isDisabled, allowSkip, buttonText } = this.state;
    const {
      gender,
      isRenux,
      isSimpleRenux,
      numberToFollow,
      onSelectedInterestsChange,
      preselect,
      selectedInterests,
      showTableOfContents,
    } = this.props;
    const fadeHeight = 40;
    const distanceFromBottom = 75;

    return (
      <Fragment>
        <Heading size="sm" overflow="normal">
          {headerText}
        </Heading>
        <Box height="100%" margin={5} overflow="hidden">
          <Box direction="column" display="flex" height="100%">
            <NuxPickerInterestsGridContainer
              gender={gender}
              handleBottomScroll={this.handleBottomScrollThrottled}
              isRenux={isRenux}
              isSimpleRenux={isSimpleRenux}
              numberToFollow={numberToFollow}
              onSelectedInterestsChange={onSelectedInterestsChange}
              preselect={preselect}
              selectedInterests={selectedInterests}
              showTableOfContents={showTableOfContents}
            />
          </Box>
          <Box
            dangerouslySetInlineStyle={{
              __style: {
                background: 'linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
                bottom: distanceFromBottom,
              },
            }}
            height={fadeHeight}
            position="absolute"
            width={MIN_HF_STEP_WIDTH}
          />
        </Box>
        <Box data-test-id="nux-picker-done-btn" padding={1}>
          <Button
            size="lg"
            color={allowSkip ? 'gray' : 'red'}
            text={buttonText}
            disabled={isDisabled}
            onClick={this.handleNextClick}
            type="submit"
          />
        </Box>
      </Fragment>
    );
  };

  render() {
    const {
      gender,
      isRenux,
      isSimpleRenux,
      onSelectedInterestsChange,
      personalizationCopy: { headerText, subHeaderText },
      preselect,
      selectedInterests,
      showTableOfContents,
      numberToFollow,
    } = this.props;
    const { isDisabled, buttonText } = this.state;

    return (
      <Experiment name="web_homefeed_nux" activate={false}>
        {({ anyEnabled: inHomefeedExp }) =>
          inHomefeedExp ? (
            this.renderOnHomefeed()
          ) : (
            <Experiment
              name="web_renux_categorized_topics"
              isEligible={isRenux && (gender === 'male' || gender === 'female')}
            >
              {({ group: limitedCategoryExp }) => {
                const limited = ['enabled_categorized', 'enabled_limited', 'employees'].includes(
                  limitedCategoryExp,
                );
                const categorized =
                  limitedCategoryExp === 'enabled_categorized' ||
                  limitedCategoryExp === 'employees';

                return (
                  <Box direction="column" display="flex" height="100%">
                    <Box display="visuallyHidden" role="alert">
                      {interpolateNamedTemplate(
                        i18n._(
                          '{{selectedTopicsCount}} of at least 5 topics selected',
                          'accessible label how many topics are selected',
                        ),
                        {
                          selectedTopicsCount: selectedInterests.length,
                        },
                      )}
                    </Box>
                    <Box paddingX={8} marginTop={8} marginBottom={categorized ? 3 : 8} wrap>
                      <Heading size="xs">{headerText}</Heading>
                      {// shipping removing subheader for control
                      // going to iterate on different subheaders so leaving in code
                      isRenux && <Text size="xl">{subHeaderText}</Text>}
                    </Box>
                    <NuxPickerInterestsGridContainer
                      categorized={categorized}
                      gender={gender}
                      handleBottomScroll={this.handleBottomScrollThrottled}
                      isRenux={isRenux}
                      isSimpleRenux={isSimpleRenux}
                      limited={limited}
                      numberToFollow={numberToFollow}
                      onSelectedInterestsChange={onSelectedInterestsChange}
                      preselect={preselect}
                      selectedInterests={selectedInterests}
                      showTableOfContents={showTableOfContents}
                    />
                    <Box position="relative">
                      {!categorized && FOOTER_FADE}
                      <Box
                        data-test-id="nux-picker-done-btn"
                        marginBottom={8}
                        marginTop={categorized ? 3 : 4}
                        paddingX={8}
                      >
                        <Button
                          size="lg"
                          color="red"
                          text={buttonText}
                          disabled={isDisabled}
                          onClick={this.handleNextClick}
                          type="submit"
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              }}
            </Experiment>
          )
        }
      </Experiment>
    );
  }
}

function mapStateToProps({ viewer }: ReduxState): ReduxStateProps {
  return {
    gender: viewer && viewer.gender ? viewer.gender : '',
  };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
  return {
    dispatchSetNuxStepProgress: (step: NuxStep) => dispatch(setNuxProgress(step)),
    dispatchUpdateFollows: (interestFollowingMapping) =>
      dispatch(updateFollowedInterests(interestFollowingMapping)),
    dispatchSubmitOrientationContext: (data) => dispatch(submitOrientationContextData(data)),
    setNuxPickerProgress: (progress) =>
      dispatch(setUserState(NUX_TOPIC_PICKER_STEP_STATUS, progress)),
  };
}

// TODO(dalvarez): Look at moving the resource call for default interests to the server,
// so that the fetched interests can be passed through the experience framework as
// extra context
export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(NuxPicker);
